import { useMemo, useState } from 'react'
import type { IntakeField, SessionCommand } from '@fitting-room/contracts'
import { checkIntakeValue } from '@fitting-room/contracts/intake'
import { isAddressField, isNameField, isPhoneField } from '@fitting-room/contracts/contact'
import { StepShell } from '../../components/RoomChrome'
import { draftStore, intakeScope, useDraftFlush } from '../../lib/drafts'
import { uiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import type { StepProps } from '../types'
import { usePendingExit } from '../usePendingExit'

/**
 * Typed input that is not a preference.
 *
 * One renderer for four of the mail's eleven sections, because the difference
 * between "Name" and "Waist" is a row of tenant data and not a screen. Nothing
 * here knows what is being collected: the label, the keyboard, the unit and
 * the validation all come off the field document.
 *
 * The input types are load-bearing rather than decorative — `email`, `tel`,
 * `number` and `date` each change the keyboard a phone puts under her thumb,
 * and this questionnaire is read on a phone.
 */
const INPUT_TYPE: Record<IntakeField['kind'], string> = {
  text: 'text',
  email: 'email',
  tel: 'tel',
  date: 'date',
  number: 'number',
  longtext: 'text',
}

/**
 * What the browser may offer to fill in.
 *
 * Keyed on what a field HOLDS, not on its `kind`: `text` is a name, a venue,
 * a nickname and eight other things, so a kind-level guess would have offered
 * her saved name for all of them. The contract already answers this question —
 * `isNameField` and friends — and answers it for the console too, so the form
 * that writes a name and the list that reads it cannot disagree.
 *
 * The one that was missing was the name, which is the field a phone is most
 * able to fill and the first one she is asked for.
 */
function autoCompleteFor(field: IntakeField): string | undefined {
  if (isNameField(field)) return 'name'
  if (isAddressField(field)) return 'email'
  if (isPhoneField(field)) return 'tel'
  return undefined
}

export function IntakeStep({ step, ctx }: StepProps<'intake'>) {
  const { locale, view, token } = ctx
  const t = uiStrings(locale)

  const drafts = useMemo(() => draftStore(token), [token])
  const scope = intakeScope(step.id)

  /**
   * What the field already holds: her own answer first, the booking second.
   *
   * This is also the baseline `command()` compares against, which is why an
   * unsent draft is NOT in it. A draft in here would read as "what it came
   * from", and the value she typed before the tab died would look unchanged
   * and never be sent.
   */
  const startingValue = (field: IntakeField): string => {
    const stored = view.session.profile[field.key]
    return stored !== undefined ? String(stored) : (view.profilePrefill[field.key] ?? '')
  }

  /** Whatever she typed here and has not sent. Read once, as the screen opens. */
  const unsent = useMemo(() => drafts.readScope(scope), [drafts, scope])

  /**
   * What the field opens with: her unsent draft, then the two above.
   *
   * `''` is a draft like any other — she cleared the field — so the test is
   * against "is there a draft" and not against emptiness. Anything looser puts
   * back the value she just deleted.
   */
  const openingValue = (field: IntakeField): string => unsent[field.key] ?? startingValue(field)

  // Lazy, so a refetch of the session cannot overwrite what she is typing —
  // and so the draft is read once, as the screen opens. Loading it in an
  // effect would write state after mount and land on top of her first
  // keystrokes, which is the same bug from the other side.
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(step.fields.map((field) => [field.key, openingValue(field)])),
  )
  // A field with a draft is one she has already typed in, so a reload comes
  // back with the same message under it rather than a quietly invalid form.
  const [touched, setTouched] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(Object.keys(unsent).map((key) => [key, true])),
  )

  const set = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }))
    setTouched((current) => (current[key] ? current : { ...current, [key]: true }))
  }

  /**
   * Only the fields she has actually typed in are kept.
   *
   * A prefilled contact field she never touched is not her typing, and writing
   * it here would copy the visit's details onto the device for no reason —
   * the same rule the Continue command below follows for the same reason.
   *
   * And nothing at all while a send is in flight, which cancels any pending
   * write before the response can retire the draft. Without it a Continue
   * pressed inside the flush window can put the draft back AFTER the server
   * accepted it, and a draft that outlives its own acceptance is one that
   * shadows whatever the boutique corrects on the pull sheet — the exact thing
   * retiring it on acceptance exists to prevent. A send that FAILS brings the
   * entries back, which is equally the point: then she still has her typing.
   */
  const drafted = useMemo(
    () =>
      ctx.pending
        ? {}
        : Object.fromEntries(
            step.fields.filter((field) => touched[field.key]).map((field) => [field.key, values[field.key] ?? '']),
          ),
    [step.fields, touched, values, ctx.pending],
  )
  useDraftFlush(drafts, scope, drafted)

  /**
   * The same check the server runs, from the same function in the contract.
   *
   * A client gating on a different rule than the server enforces is a form that
   * refuses the value it just accepted, with nothing to show for it but "that
   * did not save".
   */
  const problems: Record<string, string> = {}
  for (const field of step.fields) {
    const raw = (values[field.key] ?? '').trim()
    if (raw === '') continue
    const checked = checkIntakeValue(field, raw)
    if (!checked.ok) problems[field.key] = t.intakeProblem(checked, field.unit)
  }

  /**
   * Only what she actually changed.
   *
   * A prefilled contact field she never touched stays where it came from — the
   * visit — rather than being copied into the session, which keeps the
   * customer's details in the one table a retention purge covers. Values go as
   * she typed them, so a number that is not one is refused by the server rather
   * than arriving as the JSON `null` that `Number('x')` would become.
   */
  const changedValues = (): Record<string, string | null> => {
    const changed: Record<string, string | null> = {}
    for (const field of step.fields) {
      const raw = (values[field.key] ?? '').trim()
      const stored = view.session.profile[field.key]
      if (raw === (stored !== undefined ? String(stored) : startingValue(field)) && stored === undefined) continue
      if (stored !== undefined && raw === String(stored)) continue
      changed[field.key] = raw === '' ? null : raw
    }
    return changed
  }

  const command = (): SessionCommand => ({ type: 'profile', stepId: step.id, values: changedValues() })

  /**
   * Leaving sideways sends what she typed, but never something the server
   * would refuse: a field that fails the shared check would come back as "that
   * did not save" on a screen she has already left, which is a worse answer
   * than the draft that is holding it locally either way.
   */
  const changedFields = Object.keys(changedValues()).length > 0
  usePendingExit(ctx, changedFields && Object.keys(problems).length === 0 ? () => [command()] : null)

  const missing = step.fields.filter((f) => f.required && (values[f.key] ?? '').trim() === '')
  const canContinue = missing.length === 0 && Object.keys(problems).length === 0

  return (
    <StepShell
      title={text(step.title, locale)}
      body={text(step.body, locale)}
      progress={ctx.progress}
      locale={locale}
      skip={
        step.skippable
          ? {
              label: t.skipThis,
              disabled: ctx.pending,
              /*
               * Skip advances and writes nothing.
               *
               * It used to send every field as `null`, which the domain stores
               * as an explicit "she emptied this" — so one tap on the first
               * screen, whose contact fields arrive already filled in from the
               * booking, erased them. Skipping a form is declining to fill it
               * in, never asking for what is there to be deleted.
               */
              onSkip: () => ctx.advance(),
            }
          : undefined
      }
      footer={
        <div className="pb-2" style={{ background: 'linear-gradient(to top, var(--fr-ground-deep) 62%, transparent)' }}>
          <button
            type="button"
            className="fr-btn fr-btn-primary w-full"
            disabled={ctx.pending || !canContinue}
            onClick={() => ctx.advance([command()])}
          >
            {t.continue}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {step.fields.map((field) => {
          const id = `fr-field-${field.key}`
          const label = text(field.label, locale)
          return (
            <div key={field.key} className="flex flex-col gap-1.5">
              <label htmlFor={id} className="fr-eyebrow">
                {label}
                {field.unit ? ` · ${field.unit}` : ''}
              </label>
              {field.kind === 'longtext' ? (
                <textarea
                  id={id}
                  className="fr-input"
                  rows={4}
                  maxLength={field.maxLength}
                  placeholder={text(field.placeholder, locale)}
                  value={values[field.key] ?? ''}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              ) : (
                <input
                  id={id}
                  className="fr-input"
                  aria-invalid={touched[field.key] && problems[field.key] ? true : undefined}
                  /*
                   * `role="alert"` announces the message once, when it appears.
                   * That leaves the field itself saying only "invalid" to
                   * anyone who tabs back to it afterwards — which is exactly
                   * when she wants to hear WHY. Pointing at the message keeps
                   * the reason attached to the field it belongs to.
                   */
                  aria-describedby={touched[field.key] && problems[field.key] ? `${id}-problem` : undefined}
                  type={INPUT_TYPE[field.kind]}
                  inputMode={field.kind === 'number' ? 'numeric' : undefined}
                  autoComplete={autoCompleteFor(field)}
                  maxLength={field.kind === 'number' ? undefined : field.maxLength}
                  min={field.min}
                  max={field.max}
                  placeholder={text(field.placeholder, locale)}
                  value={values[field.key] ?? ''}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              )}
              {touched[field.key] && problems[field.key] ? (
                <p
                  id={`${id}-problem`}
                  className="text-[0.8125rem] leading-snug"
                  role="alert"
                  style={{ color: 'var(--fr-secondary)' }}
                >
                  {problems[field.key]}
                </p>
              ) : field.help ? (
                <p className="text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
                  {text(field.help, locale)}
                </p>
              ) : null}
            </div>
          )
        })}
      </div>
    </StepShell>
  )
}
